import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { CommandContribution, CommandRegistry } from '@patsnap/tahiti-core';
import { createDialog } from '@patsnap/tahiti-core';
import type { FileUploadDialogOptions, FileUploadDialogResult } from './types';

const fileUploadDialogService = createDialog(() => import('./FileUploadDialog.vue'));

/**
 * 文件上传对话框关闭的原因
 */
export interface FileUploadDialogCloseReason {
    type: 'close' | 'error';
    error?: unknown;
}

/**
 * 文件上传命令贡献
 * 提供打开文件上传对话框的命令
 */
@injectable()
export class FileUploadCommandContribution implements CommandContribution {
    /**
     * 打开文件上传对话框
     * @param payload 对话框配置选项
     * @returns Promise，resolve 时返回用户提交的数据，reject 时返回关闭原因
     */
    private _handleOpenFileUploadDialog(payload: FileUploadDialogOptions): Promise<FileUploadDialogResult> {
        return new Promise((resolve, reject) => {
            const instance = fileUploadDialogService();
            instance.onSubmit(async (data, next) => {
                try {
                    resolve(data);
                    await next();
                } catch (error) {
                    reject({ type: 'error', error } as FileUploadDialogCloseReason);
                    next(false);
                }
            });
            instance.onCancel(() => {
                reject({ type: 'close' } as FileUploadDialogCloseReason);
            });
            instance.open(payload);
        });
    }

    registerCommands(commands: CommandRegistry): void {
        commands.registerCommand({
            id: 'fileUpload:open-dialog',
        }, {
            execute: (payload?: FileUploadDialogOptions) => {
                return this._handleOpenFileUploadDialog(payload ?? {});
            },
        });
    }
}

declare module '@patsnap/tahiti-core' {
    export interface CommandService {
        /**
         * 打开文件上传对话框
         * @param commandId - 命令ID
         * @param payload - 对话框配置选项
         * @returns Promise，resolve 时返回用户提交的上传结果，reject 时返回关闭原因
         */
        executeCommand(commandId: 'fileUpload:open-dialog', payload?: FileUploadDialogOptions): Promise<FileUploadDialogResult>;
    }
}
